import { useState } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Platform } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { createStyles, addColorOpacity } from '../../shared/utils/styles';
import { DesignTokens } from '../../shared/types/tokens';
import { DownPaymentType } from '../../shared/types/mortgage';

interface MortgageCalculatorInputProps {
  propertyValue: string;
  onPropertyValueChange: (value: string) => void;
  originalPropertyValue: number | null;
  onResetPropertyValue: () => void;
  downPayment: string;
  onDownPaymentChange: (value: string) => void;
  downPaymentType: DownPaymentType;
  onDownPaymentTypeChange: (type: DownPaymentType) => void;
  interestRate: string;
  onInterestRateChange: (value: string) => void;
  loanTermYears: number;
  onLoanTermYearsChange: (years: number) => void;
  startDate: string;
  onStartDateChange: (date: string) => void;
}

export function MortgageCalculatorInput({
  propertyValue,
  onPropertyValueChange,
  originalPropertyValue,
  onResetPropertyValue,
  downPayment,
  onDownPaymentChange,
  downPaymentType,
  onDownPaymentTypeChange,
  interestRate,
  onInterestRateChange,
  loanTermYears,
  onLoanTermYearsChange,
  startDate,
  onStartDateChange,
}: MortgageCalculatorInputProps) {
  const { tokens } = useTheme();
  const utils = createStyles(tokens);
  const styles = makeStyles(tokens, utils);

  const loanTerms = [15, 20, 25, 30];
  const [customTerm, setCustomTerm] = useState('');
  const [showCustomTerm, setShowCustomTerm] = useState(false);

  // Calculate down payment conversion
  const currentPropertyValue = parseFloat(propertyValue) || 0;
  const currentDownPayment = parseFloat(downPayment) || 0;
  
  let downPaymentHint = '';
  if (currentPropertyValue > 0 && currentDownPayment > 0) {
    if (downPaymentType === 'amount') {
      const percentage = (currentDownPayment / currentPropertyValue) * 100;
      downPaymentHint = `(${percentage.toFixed(2)}% of property value)`;
    } else {
      const amount = (currentPropertyValue * currentDownPayment) / 100;
      downPaymentHint = `($${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`;
    }
  }

  const isPropertyValueModified = originalPropertyValue !== null && 
    Math.abs(parseFloat(propertyValue) - originalPropertyValue) > 0.01;

  return (
    <View style={styles.container}>
      <Text style={styles.sectionTitle}>Loan Details</Text>

      <View style={styles.inputGroup}>
        <View style={styles.labelRow}>
          <Text style={styles.label}>Property Value *</Text>
          {isPropertyValueModified && originalPropertyValue !== null && (
            <Pressable onPress={onResetPropertyValue} style={styles.resetButton}>
              <Text style={styles.resetButtonText}>Reset to ${originalPropertyValue.toLocaleString()}</Text>
            </Pressable>
          )}
        </View>
        <TextInput
          style={[
            styles.input,
            isPropertyValueModified && styles.inputModified,
          ]}
          placeholder="Enter property value"
          placeholderTextColor={addColorOpacity(tokens.color.onSurface, 0.6)}
          value={propertyValue}
          onChangeText={(text) => {
            // Allow empty, numbers, and one decimal point
            if (text === '' || /^\d*\.?\d*$/.test(text)) {
              const numValue = parseFloat(text);
              // Only update if valid number or empty, and if number, must be > 0
              if (text === '' || (!isNaN(numValue) && numValue > 0)) {
                onPropertyValueChange(text);
              }
            }
          }}
          keyboardType={Platform.OS === 'ios' ? 'numbers-and-punctuation' : 'decimal-pad'}
          inputMode="decimal"
        />
        {isPropertyValueModified && (
          <Text style={styles.modifiedHint}>
            Modified from original value of ${originalPropertyValue?.toLocaleString()}
          </Text>
        )}
      </View>

      <View style={styles.inputGroup}>
        <View style={styles.labelRow}>
          <Text style={styles.label}>Down Payment *</Text>
          <View style={styles.toggleContainer}>
            <Pressable
              onPress={() => {
                // Convert value when switching types
                if (downPayment && currentPropertyValue > 0) {
                  if (downPaymentType === 'percentage') {
                    // Converting from % to amount
                    const percentage = parseFloat(downPayment);
                    if (!isNaN(percentage)) {
                      const amount = (currentPropertyValue * percentage) / 100;
                      onDownPaymentChange(amount.toFixed(2));
                    }
                  } else {
                    // Converting from amount to %
                    const amount = parseFloat(downPayment);
                    if (!isNaN(amount)) {
                      const percentage = (amount / currentPropertyValue) * 100;
                      onDownPaymentChange(percentage.toFixed(2));
                    }
                  }
                }
                onDownPaymentTypeChange('amount');
              }}
              style={[
                styles.toggleButton,
                downPaymentType === 'amount' && styles.toggleButtonActive,
              ]}
            >
              <Text
                style={[
                  styles.toggleButtonText,
                  downPaymentType === 'amount' && styles.toggleButtonTextActive,
                ]}
              >
                Amount
              </Text>
            </Pressable>
            <Pressable
              onPress={() => {
                // Convert value when switching types
                if (downPayment && currentPropertyValue > 0) {
                  if (downPaymentType === 'amount') {
                    // Converting from amount to %
                    const amount = parseFloat(downPayment);
                    if (!isNaN(amount)) {
                      const percentage = (amount / currentPropertyValue) * 100;
                      onDownPaymentChange(percentage.toFixed(2));
                    }
                  } else {
                    // Converting from % to amount
                    const percentage = parseFloat(downPayment);
                    if (!isNaN(percentage)) {
                      const amount = (currentPropertyValue * percentage) / 100;
                      onDownPaymentChange(amount.toFixed(2));
                    }
                  }
                }
                onDownPaymentTypeChange('percentage');
              }}
              style={[
                styles.toggleButton,
                downPaymentType === 'percentage' && styles.toggleButtonActive,
              ]}
            >
              <Text
                style={[
                  styles.toggleButtonText,
                  downPaymentType === 'percentage' && styles.toggleButtonTextActive,
                ]}
              >
                %
              </Text>
            </Pressable>
          </View>
        </View>
        <TextInput
          style={styles.input}
          placeholder={downPaymentType === 'amount' ? 'Enter down payment amount' : 'Enter down payment %'}
          placeholderTextColor={addColorOpacity(tokens.color.onSurface, 0.8)}
          value={downPayment}
          onChangeText={(text) => {
            // Allow empty, numbers, and one decimal point
            if (text === '' || /^\d*\.?\d*$/.test(text)) {
              const numValue = parseFloat(text);
              
              if (text === '') {
                onDownPaymentChange(text);
              } else if (!isNaN(numValue)) {
                if (downPaymentType === 'percentage') {
                  // Percentage: must be between 0 and 100
                  if (numValue >= 0 && numValue <= 100) {
                    onDownPaymentChange(text);
                  }
                } else {
                  // Amount: must be between 0 and property value
                  if (numValue >= 0 && (currentPropertyValue === 0 || numValue <= currentPropertyValue)) {
                    onDownPaymentChange(text);
                  }
                }
              }
            }
          }}
          keyboardType={Platform.OS === 'ios' ? 'numbers-and-punctuation' : 'decimal-pad'}
          inputMode="decimal"
        />
        {downPaymentHint && (
          <Text style={styles.hint}>{downPaymentHint}</Text>
        )}
        {currentDownPayment > 0 && (
          <>
            {downPaymentType === 'percentage' && currentDownPayment > 100 && (
              <Text style={styles.validationHint}>
                ⚠️ Percentage cannot exceed 100%
              </Text>
            )}
            {downPaymentType === 'amount' && currentPropertyValue > 0 && currentDownPayment > currentPropertyValue && (
              <Text style={styles.validationHint}>
                ⚠️ Amount cannot exceed property value (${currentPropertyValue.toLocaleString()})
              </Text>
            )}
          </>
        )}
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>Interest Rate *</Text>
        <TextInput
          style={styles.input}
          placeholder="e.g., 5.5 for 5.5%"
          placeholderTextColor={addColorOpacity(tokens.color.onSurface, 0.8)}
          value={interestRate}
          onChangeText={(text) => {
            // Allow empty, numbers, and one decimal point
            if (text === '' || /^\d*\.?\d*$/.test(text)) {
              const numValue = parseFloat(text);
              // Interest rate: must be >= 0 and reasonable (< 100%)
              if (text === '' || (!isNaN(numValue) && numValue >= 0 && numValue < 100)) {
                onInterestRateChange(text);
              }
            }
          }}
          keyboardType={Platform.OS === 'ios' ? 'numbers-and-punctuation' : 'decimal-pad'}
          inputMode="decimal"
        />
        <Text style={styles.hint}>Enter as percentage (e.g., 5.5 for 5.5%)</Text>
        {interestRate && parseFloat(interestRate) >= 100 && (
          <Text style={styles.validationHint}>⚠️ Interest rate should be less than 100%</Text>
        )}
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>Loan Term *</Text>
        <View style={styles.termSelector}>
          {loanTerms.map((term) => (
            <Pressable
              key={term}
              onPress={() => {
                onLoanTermYearsChange(term);
                setShowCustomTerm(false);
                setCustomTerm('');
              }}
              style={[
                styles.termButton,
                loanTermYears === term && !showCustomTerm && styles.termButtonActive,
              ]}
            >
              <Text
                style={[
                  styles.termButtonText,
                  loanTermYears === term && !showCustomTerm && styles.termButtonTextActive,
                ]}
              >
                {term} years
              </Text>
            </Pressable>
          ))}
          <Pressable
            onPress={() => {
              setShowCustomTerm(true);
              if (customTerm) {
                const customValue = parseInt(customTerm, 10);
                if (!isNaN(customValue) && customValue > 0) {
                  onLoanTermYearsChange(customValue);
                }
              }
            }}
            style={[
              styles.termButton,
              showCustomTerm && styles.termButtonActive,
            ]}
          >
            <Text
              style={[
                styles.termButtonText,
                showCustomTerm && styles.termButtonTextActive,
              ]}
            >
              Custom
            </Text>
          </Pressable>
        </View>
        {showCustomTerm && (
          <TextInput
            style={styles.input}
            placeholder="Enter custom loan term (years)"
            placeholderTextColor={addColorOpacity(tokens.color.onSurface, 0.8)}
            value={customTerm}
            onChangeText={(text) => {
              // Allow empty, numbers only
              if (text === '' || /^\d+$/.test(text)) {
                setCustomTerm(text);
                const customValue = parseInt(text, 10);
                if (!isNaN(customValue) && customValue > 0 && customValue <= 50) {
                  onLoanTermYearsChange(customValue);
                }
              }
            }}
            keyboardType="numeric"
          />
        )}
        {showCustomTerm && customTerm && parseInt(customTerm, 10) > 50 && (
          <Text style={styles.validationHint}>
            ⚠️ Loan term should be 50 years or less
          </Text>
        )}
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>Start Date (optional)</Text>
        {typeof window !== 'undefined' && window.navigator && 'platform' in window.navigator && window.navigator.platform.includes('Mac') ? (
          <input
            type="date"
            value={startDate}
            onChange={(e: any) => onStartDateChange(e.target.value)}
            style={{
              width: '100%',
              padding: `${utils.spacing.md}px`,
              border: `1px solid ${tokens.color.border}`,
              borderRadius: `${utils.radius.md}px`,
              backgroundColor: tokens.color.background,
              color: tokens.color.onSurface,
              fontSize: utils.typography.sizes.base,
              fontFamily: utils.typography.body.fontFamily,
              boxSizing: 'border-box',
            }}
          />
        ) : (
          <TextInput
            style={styles.input}
            placeholder="YYYY-MM-DD (optional)"
            placeholderTextColor={addColorOpacity(tokens.color.onSurface, 0.8)}
            value={startDate}
            onChangeText={onStartDateChange}
          />
        )}
      </View>
    </View>
  );
}

function makeStyles(tokens: DesignTokens, utils: ReturnType<typeof createStyles>) {
  return StyleSheet.create({
    container: {
      width: '100%',
    },
    sectionTitle: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onBackground,
      marginBottom: utils.spacing.md,
      fontFamily: utils.typography.heading.fontFamily,
      lineHeight: utils.typography.sizes.base * utils.typography.lineHeight.tight,
    },
    inputGroup: {
      marginBottom: utils.spacing.md,
      width: '100%',
    },
    label: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      marginBottom: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
      letterSpacing: 0.2,
      textTransform: 'uppercase',
    },
    labelRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: utils.spacing.xs,
      flexWrap: 'wrap',
      gap: utils.spacing.xs,
    },
    resetButton: {
      paddingHorizontal: utils.spacing.xs,
      paddingVertical: 4,
      borderRadius: utils.radius.sm,
      backgroundColor: addColorOpacity(tokens.color.secondary, 0.2),
      borderWidth: 1,
      borderColor: tokens.color.secondary,
    },
    resetButtonText: {
      fontSize: utils.typography.sizes.xs,
      color: tokens.color.secondary,
      fontFamily: utils.typography.body.fontFamily,
      fontWeight: utils.typography.weights.semibold,
    },
    inputModified: {
      borderColor: tokens.color.secondary,
      borderWidth: 2,
    },
    modifiedHint: {
      fontSize: utils.typography.sizes.xs,
      color: tokens.color.secondary,
      marginTop: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
      fontStyle: 'italic',
    },
    input: {
      borderWidth: 1,
      borderColor: tokens.color.border,
      borderRadius: utils.radius.md,
      paddingHorizontal: utils.spacing.sm,
      paddingVertical: utils.spacing.sm,
      backgroundColor: tokens.color.surface,
      color: tokens.color.onSurface,
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.regular,
      fontFamily: utils.typography.body.fontFamily,
      width: '100%',
      minHeight: 40,
    },
    hint: {
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.7),
      marginTop: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
      lineHeight: utils.typography.sizes.xs * utils.typography.lineHeight.normal,
    },
    validationHint: {
      fontSize: utils.typography.sizes.xs,
      color: tokens.color.error,
      marginTop: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
      fontWeight: utils.typography.weights.semibold,
    },
    toggleContainer: {
      flexDirection: 'row',
      borderWidth: 1,
      borderColor: tokens.color.border,
      borderRadius: utils.radius.sm,
      overflow: 'hidden',
    },
    toggleButton: {
      paddingHorizontal: utils.spacing.sm,
      paddingVertical: utils.spacing.xs,
      backgroundColor: tokens.color.surface,
    },
    toggleButtonActive: {
      backgroundColor: tokens.color.primary,
    },
    toggleButtonText: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    toggleButtonTextActive: {
      color: tokens.color.onPrimary,
      fontWeight: utils.typography.weights.semibold,
    },
    termSelector: {
      flexDirection: 'row',
      gap: utils.spacing.xs,
      flexWrap: 'wrap',
    },
    termButton: {
      paddingHorizontal: utils.spacing.sm,
      paddingVertical: utils.spacing.xs,
      borderRadius: utils.radius.sm,
      backgroundColor: tokens.color.surface,
      borderWidth: 1,
      borderColor: tokens.color.border,
    },
    termButtonActive: {
      backgroundColor: tokens.color.primary,
      borderColor: tokens.color.primary,
    },
    termButtonText: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    termButtonTextActive: {
      color: tokens.color.onPrimary,
      fontWeight: utils.typography.weights.semibold,
    },
  });
}

